// ─── reviewMetrics ──────────────────────────────────────────────────────────
// Pure metric computations for the Review page. Reads:
//   • deals[]                (dbToDeal shape)
//   • listings[]             (_mlFromDb shape via listMarketListings)
//   • activityLog[]           (raw rows from `activity_log`)
//   • dateRange              { start: Date, end: Date }
//   • msas                   string[] (MSA whitelist — [] = all)
//   • brokers[]              broker_relationships rows (raw snake_case)
//
// Nothing in here touches the DB — all reads happen in the ReviewPage
// container and the results are passed down. Keeping the compute layer
// pure makes it easy to add unit tests later and swaps XLSX/JSON exports
// over the same numbers the UI shows.

import { STAGE_ORDER, KILLED_STAGES } from "../constants/dealStages";

// The pipeline is ordered by STAGE_ORDER (lower number = "further along").
// We invert that here so "higher rank = deeper in the funnel" reads more
// naturally in metric names. See constants/dealStages.js.
const STAGE_RANK = Object.fromEntries(
  Object.entries(STAGE_ORDER).map(([stage, priority]) => [stage, -priority])
);

// Named milestones the funnel counts against. A deal that reached
// "Underwriting" also counts toward "Sourced" and "Screened".
export const FUNNEL_TIERS = [
  { key: "sourced",     label: "Sourced" },       // any deal or listing
  { key: "screened",    label: "Screened" },      // Screening or later
  { key: "underwritten", label: "Underwritten" }, // Underwriting or later
  { key: "loi",         label: "LOI Submitted" }, // LOI or later
  { key: "diligence",   label: "In Diligence" },  // won
];

const STAGE_FOR_TIER = {
  screened:    "Screening",
  underwritten: "Underwriting",
  loi:         "LOI Submitted",
  diligence:   "In Diligence",
};

// Utility: does the given stage rank at or beyond the target milestone?
function reached(currentStage, targetStage) {
  if (!currentStage || !targetStage) return false;
  const cur = STAGE_RANK[currentStage];
  const tgt = STAGE_RANK[targetStage];
  if (cur == null || tgt == null) return false;
  return cur >= tgt;
}

// Compute, for each deal, the "max stage reached." Kills often collapse a
// deal to Passed / Lost / Dead — but the deal really did pass through UW
// or LOI on its way. Reconstructing the trajectory from stage_changed
// events gives the funnel the truth.
export function computeMaxStageReached(deal, activityLog) {
  const currentRank = STAGE_RANK[deal.stage] ?? -Infinity;
  let maxStage = deal.stage;
  let maxRank = currentRank;

  for (const evt of activityLog) {
    if (evt.entity_id !== deal.id || evt.action !== "stage_changed") continue;
    const seenStage = evt.metadata?.new_stage || evt.metadata?.old_stage;
    const rank = STAGE_RANK[seenStage];
    if (rank == null) continue;
    if (rank > maxRank) { maxRank = rank; maxStage = seenStage; }
  }

  // Killed deals count as having reached whatever stage they were IN just
  // before being killed. If the current stage is Passed/Lost/Dead and we
  // never saw a deeper stage in the log, fall back to the pre-kill event.
  if (KILLED_STAGES.includes(maxStage)) {
    // find the last non-killed stage
    const killEvent = activityLog.find(e => e.entity_id === deal.id
      && e.action === "stage_changed"
      && KILLED_STAGES.includes(e.metadata?.new_stage));
    if (killEvent) {
      const preKillStage = killEvent.metadata?.old_stage;
      const preRank = STAGE_RANK[preKillStage];
      if (preRank != null && preRank > maxRank) { maxRank = preRank; maxStage = preKillStage; }
    }
  }
  return maxStage;
}

// ── Filters shared across all tabs ────────────────────────────────
function inDateRange(dateStr, { start, end }) {
  if (!dateStr) return false;
  if (!start && !end) return true;
  const t = new Date(dateStr).getTime();
  if (!Number.isFinite(t)) return false;
  if (start && t < start.getTime()) return false;
  if (end   && t > end.getTime())   return false;
  return true;
}

function inMsa(rowMsas, msaFilter) {
  if (!msaFilter || msaFilter.length === 0) return true;
  const set = new Set(msaFilter);
  return (rowMsas || []).some(m => set.has(m));
}

// ── Market funnel per MSA ─────────────────────────────────────────
// Rows: one per MSA + a "Total" summary row.
// Columns:
//   listings_hit_market      market_listings with date_hit_market ∈ range
//   deals_created            deals with created_at ∈ range
//   screened / underwritten / loi / diligence   counts of deals that
//     reached each tier (via max-stage-reached)
//   killed_passed / killed_lost / killed_dead   terminal-stage counts
// Also emits conversion rates: screened → uw, uw → loi, loi → dili.
export function computeMarketFunnel({ deals, listings, activityLog, dateRange, msas }) {
  // Bucket by MSA. Cross-metro rows appear in every MSA they touch.
  const msaKeys = new Set(msas && msas.length > 0 ? msas : []);
  if (msaKeys.size === 0) {
    (deals || []).forEach(d => (d.msas || []).forEach(m => msaKeys.add(m)));
    (listings || []).forEach(l => (l.msas || []).forEach(m => msaKeys.add(m)));
  }

  const empty = () => ({
    listings_hit_market: 0,
    deals_created: 0,
    screened: 0, underwritten: 0, loi: 0, diligence: 0,
    killed_passed: 0, killed_lost: 0, killed_dead: 0,
  });

  const rows = {};
  const total = empty();
  for (const m of msaKeys) rows[m] = empty();

  // Listings
  for (const l of listings || []) {
    if (!inDateRange(l.dateHitMarket, dateRange)) continue;
    total.listings_hit_market++;
    (l.msas || []).forEach(m => {
      if (rows[m]) rows[m].listings_hit_market++;
    });
  }

  // Deals
  for (const d of deals || []) {
    if (!inDateRange(d.createdAt, dateRange)) continue;
    total.deals_created++;
    const maxStage = computeMaxStageReached(d, activityLog || []);
    (d.msas || []).forEach(m => {
      if (!rows[m]) return;
      const bucket = rows[m];
      bucket.deals_created++;
      if (reached(maxStage, STAGE_FOR_TIER.screened))     bucket.screened++;
      if (reached(maxStage, STAGE_FOR_TIER.underwritten)) bucket.underwritten++;
      if (reached(maxStage, STAGE_FOR_TIER.loi))          bucket.loi++;
      if (reached(maxStage, STAGE_FOR_TIER.diligence))    bucket.diligence++;
      if (d.stage === "Passed") bucket.killed_passed++;
      if (d.stage === "Lost")   bucket.killed_lost++;
      if (d.stage === "Dead")   bucket.killed_dead++;
    });
    // Also bump total's non-created milestones exactly once per deal.
    if (reached(maxStage, STAGE_FOR_TIER.screened))     total.screened++;
    if (reached(maxStage, STAGE_FOR_TIER.underwritten)) total.underwritten++;
    if (reached(maxStage, STAGE_FOR_TIER.loi))          total.loi++;
    if (reached(maxStage, STAGE_FOR_TIER.diligence))    total.diligence++;
    if (d.stage === "Passed") total.killed_passed++;
    if (d.stage === "Lost")   total.killed_lost++;
    if (d.stage === "Dead")   total.killed_dead++;
  }

  const withRates = (r) => ({
    ...r,
    conv_screen_to_uw:  r.screened     > 0 ? (r.underwritten / r.screened)  * 100 : null,
    conv_uw_to_loi:     r.underwritten > 0 ? (r.loi           / r.underwritten) * 100 : null,
    conv_loi_to_dili:   r.loi          > 0 ? (r.diligence     / r.loi)      * 100 : null,
    conv_created_to_uw: r.deals_created > 0 ? (r.underwritten / r.deals_created) * 100 : null,
    win_rate:           (r.loi + r.diligence) > 0 ? (r.diligence / (r.loi + r.diligence)) * 100 : null,
  });

  const perMsa = Object.entries(rows).map(([msa, r]) => ({ msa, ...withRates(r) }))
    .sort((a, b) => (b.deals_created - a.deals_created) || a.msa.localeCompare(b.msa));

  return {
    total: { msa: "All markets", ...withRates(total) },
    perMsa,
  };
}

// ── Merged deals + listings table ─────────────────────────────────
// Rows carry a `kind` = "deal" | "listing" so the UI can render actions
// appropriately. Listings that have deal_id set are elided (they'll show
// up in the deal row) unless includeLinkedListings is set.
export function computeDealsAndListings({ deals, listings, dateRange, msas, includeLinkedListings = false }) {
  const dealRows = (deals || [])
    .filter(d => inDateRange(d.createdAt, dateRange) && inMsa(d.msas, msas))
    .map(d => ({
      kind: "deal",
      id: d.id,
      created_at: d.createdAt,
      msas: d.msas || [],
      address: d.addresses?.[0] || "",
      stage: d.stage,
      buyBoxVerdict: d.buyBoxVerdict,
      buyBoxSource: d.buyBoxSource,
      buyBoxReason: d.buyBoxReason,
      sf: d.squareFeet,
      askingPrice: d.askingPrice,
      capRate: d.capRate,
      broker: [d.brokerName, d.brokerFirm, d.brokerEmail].filter(Boolean).join(" · "),
      killedReason: d.killedReason,
      killedAt: d.killedAt,
      timeToLoiDays: d.timeToLoiDays,
      timeInUwDays: d.timeInUwDays,
      _raw: d,
    }));

  const listingRows = (listings || [])
    .filter(l => (includeLinkedListings || !l.dealId) && inDateRange(l.dateHitMarket, dateRange) && inMsa(l.msas, msas))
    .map(l => ({
      kind: "listing",
      id: l.id,
      created_at: l.dateHitMarket || l.createdAt,
      msas: l.msas || [],
      address: l.addresses?.[0] || (l.city ? `${l.city}, ${l.state}` : ""),
      stage: null,
      buyBoxVerdict: l.buyBoxVerdict,
      buyBoxSource: l.buyBoxSource,
      buyBoxReason: l.buyBoxReason,
      sf: l.sf,
      askingPrice: l.askingPrice,
      capRate: l.capRateMarket,
      broker: l.brokerContactEmail || l.brokerFirm,
      brokerFirm: l.brokerFirm,
      dealId: l.dealId,
      _raw: l,
    }));

  return [...dealRows, ...listingRows].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
}

// ── Bias / self-review dashboard ──────────────────────────────────
// Answers questions like:
//   • Buy-box discipline: what % of LOI-submitted deals were in-box at
//     submission? out-of-box submissions are the "you know better" flag.
//   • Win rate: of deals that made LOI, how many closed vs lost?
//   • Kill mix: distribution of killed_reason across Passed / Lost / Dead
//   • Bid delta: average $/SF gap between our final bid and the winner's
//     when we lost — signals whether we were "close but under" or
//     "way off."
//   • Time metrics: median time_to_loi, time_in_uw
export function computeBiasMetrics({ deals, competition, dateRange, msas }) {
  const inScope = (deals || []).filter(d =>
    inDateRange(d.createdAt, dateRange) && inMsa(d.msas, msas));

  const submitted = inScope.filter(d => STAGE_RANK[d.stage] >= STAGE_RANK["LOI Submitted"]);
  const submittedInBox = submitted.filter(d => d.buyBoxVerdict === "in_box");
  const submittedFiltered = submitted.filter(d => d.buyBoxVerdict === "filtered");
  const submittedUndecided = submitted.filter(d => d.buyBoxVerdict === "undecided");

  const won = inScope.filter(d => d.stage === "In Diligence" || d.stage === "Acquired");
  const lost = inScope.filter(d => d.stage === "Lost");
  const passed = inScope.filter(d => d.stage === "Passed");
  const dead = inScope.filter(d => d.stage === "Dead");

  const reasonMix = { Passed: {}, Lost: {}, Dead: {} };
  for (const d of [...passed, ...lost, ...dead]) {
    if (!d.killedReason) continue;
    const bucket = reasonMix[d.stage];
    if (bucket) bucket[d.killedReason] = (bucket[d.killedReason] || 0) + 1;
  }

  // Bid deltas from deal_competition — keyed by deal_id
  const compByDeal = new Map((competition || []).map(c => [c.deal_id, c]));
  const bidDeltas = lost
    .map(d => compByDeal.get(d.id))
    .filter(c => c && c.delta_pct != null)
    .map(c => Number(c.delta_pct));

  // Timing medians (skip nulls)
  const median = (arr) => {
    const clean = arr.filter(n => n != null && Number.isFinite(n)).sort((a, b) => a - b);
    if (clean.length === 0) return null;
    const mid = Math.floor(clean.length / 2);
    return clean.length % 2 === 0 ? (clean[mid - 1] + clean[mid]) / 2 : clean[mid];
  };
  const medianTimeToLoi = median(submitted.map(d => d.timeToLoiDays));
  const medianTimeInUw  = median(inScope.map(d => d.timeInUwDays));

  return {
    counts: {
      inScope: inScope.length,
      submitted: submitted.length,
      won: won.length,
      lost: lost.length,
      passed: passed.length,
      dead: dead.length,
    },
    buyBoxDiscipline: {
      submitted: submitted.length,
      submittedInBox: submittedInBox.length,
      submittedFiltered: submittedFiltered.length,
      submittedUndecided: submittedUndecided.length,
      pctInBoxOfSubmitted: submitted.length > 0
        ? (submittedInBox.length / submitted.length) * 100
        : null,
    },
    winRate: {
      won: won.length,
      lost: lost.length,
      winRatePct: (won.length + lost.length) > 0
        ? (won.length / (won.length + lost.length)) * 100
        : null,
    },
    reasonMix,
    bidDelta: {
      count: bidDeltas.length,
      avgPct: bidDeltas.length > 0 ? bidDeltas.reduce((s, x) => s + x, 0) / bidDeltas.length : null,
      medianPct: median(bidDeltas),
    },
    timing: {
      medianTimeToLoi,
      medianTimeInUw,
    },
  };
}


// ── Review Pipeline funnel (2025-slide layout) ────────────────────
// The new default view for the Review Pipeline tab. Bucketed to match
// the 2025 REVIEW slide: three top-level lanes (Dead / Tracking /
// Active), each Dead+Active lane further split Off- vs On-market, and
// under each Off/On split we count deals that made LOI and Best-&-Final.
//
// Bucketing rules:
//   • Dead lane        = current stage ∈ KILLED_STAGES (Passed, Lost, Dead)
//     - byStage        = per-stage counts within Dead
//   • Tracking lane    = current stage === "Tracking"
//   • Active lane      = every other stage (Outreach, Unsolicited Offer,
//                        Screening, Underwriting, LOI Submitted, In Diligence)
//     - byStage        = per-stage counts within Active (excluding
//                        Outreach & Unsolicited Offer — grouped as "Sourcing")
//   • For each lane's Off/On splits, "loi" counts deals whose max stage
//     reached ≥ LOI Submitted; "bestFinal" counts deals with
//     deal.madeBestAndFinal === true.
export function computeReviewPipelineFunnel({ deals, activityLog, dateRange, msas, preProxySummaries = [] }) {
  const inScope = (deals || []).filter(d =>
    inDateRange(d.createdAt, dateRange) && inMsa(d.msas, msas));

  const makeSplit = () => ({ total: 0, lois: 0, bestFinal: 0 });
  const makeLane = () => ({
    total: 0,
    byStage: {},
    offMarket: makeSplit(),
    onMarket:  makeSplit(),
  });

  const lanes = {
    dead:     makeLane(),
    tracking: makeLane(),
    active:   makeLane(),
  };

  for (const d of inScope) {
    const laneKey =
      KILLED_STAGES.includes(d.stage) ? "dead" :
      d.stage === "Tracking"           ? "tracking" :
      "active";
    const lane = lanes[laneKey];
    lane.total++;
    lane.byStage[d.stage] = (lane.byStage[d.stage] || 0) + 1;

    // Skip Off/On breakdown for the Tracking lane — slide doesn't show it.
    if (laneKey === "tracking") continue;

    const bucket = d.marketed ? lane.onMarket : lane.offMarket;
    bucket.total++;

    const maxStage = computeMaxStageReached(d, activityLog || []);
    if (reached(maxStage, "LOI Submitted")) bucket.lois++;
    if (d.madeBestAndFinal)                 bucket.bestFinal++;
  }

  // Merge in any pre-Proxy summaries that overlap the requested date range.
  // Each pre-Proxy row carries its own [period_start, period_end] and the
  // slide-shaped counts JSONB. We add them onto the live tally so the
  // funnel shows a single unified total when 2025 is in the filter window.
  const preProxyMerged = [];
  for (const row of preProxySummaries) {
    if (!overlapsRange(row.period_start, row.period_end, dateRange)) continue;
    preProxyMerged.push(row);
    const c = row.counts || {};
    const addLane = (laneKey, srcKey) => {
      const src = c[srcKey];
      if (!src) return;
      const lane = lanes[laneKey];
      lane.total += (src.total || 0);
      if (laneKey === "tracking") return;
      lane.offMarket.total     += (src.off_market || 0);
      lane.offMarket.lois      += (src.loi_off    || 0);
      lane.offMarket.bestFinal += (src.bf_off     || 0);
      lane.onMarket.total      += (src.on_market  || 0);
      lane.onMarket.lois       += (src.loi_on     || 0);
      lane.onMarket.bestFinal  += (src.bf_on      || 0);
    };
    addLane("dead",     "dead");
    addLane("tracking", "tracking");
    addLane("active",   "active");
  }

  const total = lanes.dead.total + lanes.tracking.total + lanes.active.total;

  return { total, lanes, preProxyMerged };
}

// Overlap check for pre-proxy summary rows against the filter range.
function overlapsRange(startStr, endStr, dateRange) {
  if (!dateRange || (!dateRange.start && !dateRange.end)) return true;
  const rs = startStr ? new Date(startStr).getTime() : -Infinity;
  const re = endStr   ? new Date(endStr).getTime()   :  Infinity;
  const fs = dateRange.start ? dateRange.start.getTime() : -Infinity;
  const fe = dateRange.end   ? dateRange.end.getTime()   :  Infinity;
  return rs <= fe && re >= fs;
}

// ── Date-range presets ────────────────────────────────────────────
// Callers can pass one of the string presets ("this_quarter", "ytd", …)
// or an object of the shape:
//   { kind: "days_ago", n: 30 }
//   { kind: "quarter", year: 2025, q: 2 }
//   { kind: "custom", start: "2025-01-01", end: "2025-12-31" }
export function makeDateRange(preset, refDate = new Date()) {
  const d = new Date(refDate);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const startOfMonth = (yy, mm) => new Date(Date.UTC(yy, mm, 1, 0, 0, 0));
  const endOfMonth   = (yy, mm) => new Date(Date.UTC(yy, mm + 1, 0, 23, 59, 59));
  const endOfDay     = (yy, mm, dd) => new Date(Date.UTC(yy, mm, dd, 23, 59, 59));

  // Object presets — F2 filter shape (days_ago / quarter / custom)
  if (preset && typeof preset === "object") {
    if (preset.kind === "days_ago" && Number.isFinite(Number(preset.n))) {
      const n = Number(preset.n);
      const start = new Date(d.getTime() - n * 24 * 60 * 60 * 1000);
      return { start, end: d, label: `Last ${n} days` };
    }
    if (preset.kind === "quarter" && Number.isFinite(Number(preset.year)) && Number.isFinite(Number(preset.q))) {
      const yy = Number(preset.year), qi = Math.min(Math.max(Number(preset.q), 1), 4);
      const mm = (qi - 1) * 3;
      return { start: startOfMonth(yy, mm), end: endOfMonth(yy, mm + 2), label: `Q${qi} ${yy}` };
    }
    if (preset.kind === "custom") {
      const s = preset.start ? new Date(preset.start) : null;
      const e = preset.end   ? new Date(`${preset.end}T23:59:59Z`) : null;
      const label = preset.start && preset.end
        ? `${String(preset.start).slice(0,10)} → ${String(preset.end).slice(0,10)}`
        : "Custom range";
      return { start: s, end: e, label };
    }
  }

  switch (preset) {
    case "this_quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return { start: startOfMonth(y, qStart), end: endOfMonth(y, qStart + 2), label: `${["Q1","Q2","Q3","Q4"][qStart / 3]} ${y}` };
    }
    case "last_quarter": {
      const qStart = Math.floor(m / 3) * 3 - 3;
      const yy = qStart < 0 ? y - 1 : y;
      const mm = qStart < 0 ? qStart + 12 : qStart;
      return { start: startOfMonth(yy, mm), end: endOfMonth(yy, mm + 2), label: `${["Q1","Q2","Q3","Q4"][mm / 3]} ${yy}` };
    }
    case "ytd":
      return { start: startOfMonth(y, 0), end: endOfDay(y, m, d.getUTCDate()), label: `YTD ${y}` };
    case "ttm": {
      const start = new Date(Date.UTC(y - 1, m, 1, 0, 0, 0));
      return { start, end: d, label: "Trailing 12 months" };
    }
    case "all":
    default:
      return { start: null, end: null, label: "All time" };
  }
}
